import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import "../css/LoginPage.css";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import { Button, Form, Modal } from "react-bootstrap";
import { FiArrowLeft, FiCheckCircle, FiEye, FiEyeOff } from "react-icons/fi";
import { setUser } from "../redux/userSlice";
import { API_URL, API_URL_All } from "../api";
import * as general from '../libs/general';

const NETWORK_ERROR_MESSAGE =
  "Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng hoặc thử lại khi máy chủ đã sẵn sàng.";

const SERVER_ERROR_MESSAGE =
  "Máy chủ đang gặp sự cố hoặc không phản hồi. Vui lòng thử lại sau.";

function isLikelyNetworkError(err) {
  if (!err) return false;
  const name = String(err.name || "");
  const msg = String(err.message || "");
  return (
    name === "TypeError" ||
    /failed to fetch|networkerror|load failed|aborted|ecconnrefused|net::/i.test(msg)
  );
}

const SETUP_PASSWORD_HINT =
  "User này thuộc diện cần thiết lập mật khẩu. Vui lòng bấm 'Thiết lập mật khẩu' hoặc đăng nhập bằng Google sign in.";

const WRONG_LOGIN_MESSAGE = "Thông tin đăng nhập không đúng.";

/** Lấy đúng nội dung server trả về. Không có nội dung thì là lỗi hệ thống. */
function messageFromLoginResponse(raw) {
  let data = {};
  let message = "";
  const text = typeof raw === "string" ? raw.trim() : "";
  if (text) {
    try {
      data = JSON.parse(text);
      if (typeof data?.message === "string") message = data.message.trim();
    } catch {
      if (!text.startsWith("<")) message = text;
    }
  }
  if (!message) {
    message = SERVER_ERROR_MESSAGE;
  }
  return { data, message };
}

const GoogleIcon = () => (
  <svg className="login-sso-actions__google-icon" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

const LoginPage = () => {
  const [account, setAccount] = useState(""); // Email hoặc tên đăng nhập
  const [password, setPassword] = useState(""); // State lưu trữ mật khẩu, giá trị mặc định là "zto123"
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [error, setError] = useState(""); // State lưu trữ thông báo lỗi
  const [showSetupPassword, setShowSetupPassword] = useState(false);
  const [setupStep, setSetupStep] = useState(1);
  const [setupIdentifier, setSetupIdentifier] = useState("");
  const [setupPassword, setSetupPassword] = useState("");
  const [setupPasswordConfirm, setSetupPasswordConfirm] = useState("");
  const [setupError, setSetupError] = useState("");
  const [setupSuccess, setSetupSuccess] = useState(false);
  const googleOverlayRef = useRef(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(240);
  const dispatch = useDispatch();  // Hook để dispatch action

  useEffect(() => {
    const container = googleOverlayRef.current?.parentElement;
    if (!container) return undefined;

    const updateWidth = () => {
      setGoogleBtnWidth(Math.max(container.clientWidth, 200));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const openSetupPassword = () => {
    setShowSetupPassword(true);
    setSetupStep(1);
    setSetupIdentifier("");
    setSetupPassword("");
    setSetupPasswordConfirm("");
    setSetupError("");
    setSetupSuccess(false);
  };

  const closeSetupPassword = () => {
    setShowSetupPassword(false);
  };

  const resolveSetupEligibility = async (identifier) => {
    const key = (identifier || "").trim();
    try {
      const res = await fetch(
        `${API_URL_All}/api/users/login-status?identifier=${encodeURIComponent(key)}`
      );
      const raw = await res.text();
      let body = null;
      if (raw) {
        try {
          body = JSON.parse(raw);
        } catch {
          if (res.status >= 500) {
            return { ok: false, message: SERVER_ERROR_MESSAGE };
          }
        }
      }
      if (res.ok && body?.passwordSetupRequired === true) {
        return { ok: true };
      }
      if (res.status >= 500) {
        return { ok: false, message: SERVER_ERROR_MESSAGE };
      }
      return { ok: false, message: "User này không thuộc diện cần thiết lập mật khẩu." };
    } catch (err) {
      if (isLikelyNetworkError(err)) {
        return { ok: false, message: NETWORK_ERROR_MESSAGE };
      }
      return { ok: false, message: "User này không thuộc diện cần thiết lập mật khẩu." };
    }
  };

  const handleSetupStep1 = async () => {
    const result = await resolveSetupEligibility(setupIdentifier);
    if (!result.ok) {
      setSetupError(result.message);
      return;
    }
    setSetupError("");
    setSetupStep(2);
  };

  const handleSetupFinish = async () => {
    if (!setupPassword || !setupPasswordConfirm) {
      setSetupError("Vui lòng nhập đầy đủ mật khẩu.");
      return;
    }
    if (setupPassword !== setupPasswordConfirm) {
      setSetupError("Mật khẩu và Nhập lại mật khẩu không trùng khớp.");
      return;
    }
    try {
      const res = await fetch(`${API_URL_All}/api/users/setupPassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: setupIdentifier, password: setupPassword }),
      });
      const raw = await res.text();
      let result = {};
      if (raw) {
        try {
          result = JSON.parse(raw);
        } catch {
          if (!res.ok && res.status >= 500) {
            setSetupError(SERVER_ERROR_MESSAGE);
            return;
          }
        }
      }
      if (!res.ok || !result?.success) {
        if (res.status >= 500) {
          setSetupError(SERVER_ERROR_MESSAGE);
          return;
        }
        setSetupError(result.message || "Thiết lập mật khẩu thất bại.");
        return;
      }
      setSetupError("");
      setSetupSuccess(true);
    } catch (err) {
      setSetupError(
        isLikelyNetworkError(err) ? NETWORK_ERROR_MESSAGE : "Thiết lập mật khẩu thất bại."
      );
    }
  };

  const responseGoogle = async (response) => {
    if (response.error) {
      console.error("Google Login Failed:", response.error);
      alert("Đăng nhập Google thất bại. Vui lòng thử lại!");
      return;
    }

    const { credential } = response;

    if (!credential) {
      console.error("Không nhận được token từ Google:", response);
      alert("Không nhận được token từ Google.");
      return;
    }

    try {
      // console.log("Sending token to backend:", credential); 
      const res = await fetch(`${API_URL_All}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credential }),
      });

      const data = await res.json();
      // console.log("Server Response:", data); 

        if (data.success) {
        // Đảm bảo dữ liệu trả về chứa thông tin người dùng và role
        const cfg = data.data?.config || {};
        const userId = data.data?.id ?? cfg.userId;
        const userData = {
          ...data.data, // Lấy toàn bộ thông tin người dùng từ data.data
          id: userId,
          userId,
          name: cfg.name || data.data?.name,
          role: "user", // Gán quyền "user" cho tài khoản này
          };
        general.setLocalStorage("user_config", cfg);
        localStorage.setItem('token', credential);
        dispatch(setUser(userData)); // Lưu thông tin người dùng vào Redux
        navigate("/trang-chu"); // Chuyển đến trang chính cho người dùng
      } else {
        alert("Đăng nhập thất bại: " + (data.data || "Lỗi không xác định từ server"));
      }

    } catch (error) {
      console.error(error);
      alert("Có lỗi xảy ra. Vui lòng thử lại!");
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const loginIdentifier = (account || "").trim();
    setError("");

    try {
      const userRes = await fetch(
        `${API_URL_All}/api/users/login-status?identifier=${encodeURIComponent(loginIdentifier)}`
      );
      if (userRes.ok) {
        const status = await userRes.json();
        if (status?.passwordSetupRequired === true) {
          setError(SETUP_PASSWORD_HINT);
          return;
        }
      }
    } catch (error) {
      // ignore pre-check error, keep login flow
    }

    // Gửi yêu cầu đến API để xác thực người dùng
    try {
      const clientHost = typeof window !== "undefined" ? window.location.hostname : "";
      const res = await fetch(`${API_URL_All}/api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Host": clientHost,
        },
        body: JSON.stringify({ email: loginIdentifier, password }),
      });

      const raw = await res.text();
      const { data, message } = messageFromLoginResponse(raw);

      if (res.ok) {
        const roleFromServer = String(data?.role || "").toLowerCase();
        const isAdmin = data?.isAdmin === true || data?.admin === true;
        const isMember = !isAdmin && (data?.isMember === true || data?.member === true);
        const cfg = data.config || {};
        const userId = data.id ?? cfg.userId;
        const userData = {
          email: data.email,
          id: userId,
          userId,
          name: cfg.name || data.email,
          role: roleFromServer || "user",
          isAdmin,
          isMember,
        };
        general.setLocalStorage("user_config", cfg);
        localStorage.setItem('token', data.token);
        dispatch(setUser(userData));
        navigate(isAdmin || isMember ? "/admin-welcome" : "/trang-chu"); // Điều hướng theo isAdmin/isMember
      } else if (res.status === 401) {
        setError(WRONG_LOGIN_MESSAGE);
      } else {
        setError(message);
      }

    } catch (error) {
      console.error("Error during login:", error);
      setError(isLikelyNetworkError(error) ? NETWORK_ERROR_MESSAGE : SERVER_ERROR_MESSAGE);
    }
  };

  return (
    <div>
      <div className="header" >
        <Header />
      </div>

      <div className="container my-5">
        <div className="border rounded shadow-lg bg-white p-4">
          <div className="row align-items-stretch">
            {/* Bên trái: Hình ảnh */}
            <div className="col-md-6 d-flex p-0">
              <img
                src="https://media.vietq.vn/files/HanHien/2023/05/03/logistic.jpg"
                alt="Login"
                className="img-fluid w-100 rounded-start"
                style={{
                  objectFit: "cover",
                  height: "100%",
                  maxHeight: "600px", // Đảm bảo chiều cao phù hợp
                  minHeight: "100%",
                }}
              />
            </div>

            {/* Bên phải: Form đăng nhập */}
            <div className="col-md-6 d-flex align-items-center">
              <div className="p-4 w-100">
                <h2 className="text-center text-uppercase font-weight-bold mb-4">
                  Đăng nhập
                </h2>

                {/* Form đăng nhập */}
                <form onSubmit={handleSubmit}>
                  {/* Tên đăng nhập (email) */}

                  {error && (
                    <div className="alert alert-danger mt-3" role="alert">
                      {error}
                    </div>
                  )}
                  <div className="mb-3">
                    <label htmlFor="txtAccount" className="form-label">
                      Email hoặc Tên đăng nhập <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="txtAccount"
                      className="form-control"
                      placeholder="Nhập email hoặc tên đăng nhập"
                      value={account}
                      onChange={(e) => setAccount(e.target.value)}
                      required
                    />
                  </div>

                  {/* Mật khẩu */}
                  <div className="mb-3">
                    <label htmlFor="txtPassword" className="form-label">
                      Mật khẩu <span className="text-danger">*</span>
                    </label>
                    <div className="login-password-field">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="txtPassword"
                        className="form-control"
                        placeholder="Nhập mật khẩu"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="login-password-field__toggle"
                        onClick={() => setShowPassword((visible) => !visible)}
                        aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  {/* Ghi nhớ mật khẩu */}
                  <div className="mb-3 d-flex justify-content-between">
                    <div>
                      <input
                        type="checkbox"
                        id="chkSave"
                        className="form-check-input"
                      />
                      <label htmlFor="chkSave" className="ms-2">
                        Lưu mật khẩu
                      </label>
                    </div>
                  </div>

                  {/* Button Đăng nhập */}
                  <div className="text-center mb-3">
                    <button
                      type="submit"
                      className="btn btn-primary w-100 fw-bold"
                    >
                      Đăng nhập
                    </button>
                  </div>
                  <hr className="login-divider my-3" />
                  <div className="login-sso-actions">
                    <div className="login-sso-actions__google">
                      <div
                        className="btn btn-outline-secondary w-100 login-sso-actions__btn login-sso-actions__google-btn login-sso-actions__google-visual"
                        aria-hidden="true"
                      >
                        <GoogleIcon />
                        <span>Sign in with Google</span>
                      </div>
                      <div ref={googleOverlayRef} className="login-sso-actions__google-overlay">
                        <GoogleLogin
                          onSuccess={responseGoogle}
                          onError={() => console.log("Login Failed")}
                          size="large"
                          theme="outline"
                          width={googleBtnWidth}
                        />
                      </div>
                    </div>
                    <div className="login-sso-actions__setup">
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-100 login-sso-actions__btn"
                        onClick={openSetupPassword}
                      >
                        Thiết lập mật khẩu
                      </button>
                    </div>
                  </div>

                  {/* Liên kết Đăng ký */}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showSetupPassword} onHide={closeSetupPassword} centered>
        <Modal.Header closeButton>
          <Modal.Title>Thiết lập mật khẩu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {setupSuccess ? (
            <div className="text-center py-2">
              <FiCheckCircle size={38} className="text-success mb-2" />
              <div className="fw-semibold mb-2">Thiết lập mật khẩu thành công.</div>
              <Button size="sm" onClick={closeSetupPassword}>
                <FiArrowLeft className="me-1" />
                Trở về trang đăng nhập
              </Button>
            </div>
          ) : (
            <>
              {setupStep === 1 && (
                <Form.Group>
                  <Form.Label>Nhập tên đăng nhập hoặc email</Form.Label>
                  <Form.Control
                    value={setupIdentifier}
                    onChange={(e) => {
                      setSetupIdentifier(e.target.value);
                      setSetupError("");
                    }}
                  />
                </Form.Group>
              )}
              {setupStep === 2 && (
                <div className="row g-3">
                  <div className="col-12">
                    <span className="text-muted">
                      Tên đăng nhập: <strong>{setupIdentifier}</strong>
                    </span>
                  </div>
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label>Nhập mật khẩu</Form.Label>
                      <Form.Control
                        type="password"
                        value={setupPassword}
                        onChange={(e) => {
                          setSetupPassword(e.target.value);
                          setSetupError("");
                        }}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label>Nhập lại mật khẩu</Form.Label>
                      <Form.Control
                        type="password"
                        value={setupPasswordConfirm}
                        onChange={(e) => {
                          setSetupPasswordConfirm(e.target.value);
                          setSetupError("");
                        }}
                      />
                    </Form.Group>
                  </div>
                </div>
              )}
              {setupError && (
                <div className="alert alert-danger mt-3 mb-0 py-2">{setupError}</div>
              )}
            </>
          )}
        </Modal.Body>
        {!setupSuccess && (
          <Modal.Footer>
            <Button size="sm" variant="secondary" onClick={closeSetupPassword}>Đóng</Button>
            {setupStep === 1 && (
              <Button size="sm" onClick={handleSetupStep1}>Tiếp theo</Button>
            )}
            {setupStep === 2 && (
              <Button size="sm" onClick={handleSetupFinish}>Hoàn tất</Button>
            )}
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default LoginPage;
